import { useMemo } from "react";
import { graphql } from "../__generated__/mcs_backend_v1";

import { useQuery } from "@tanstack/react-query";
import { mcsGqlClient } from "../utils/mcsGqlClient";

import type { SelectInputChoice } from "../components/SelectInput/SelectInput";

export const LIST_INSURANCE_PROVIDERS = graphql(`
  query ListInsuranceProviders {
    allInsuranceProviders {
      edges {
        node {
          id
          slug
          niceName
          mcsPartner {
            slug
            niceName
          }
        }
      }
    }
  }
`);

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const useAllInsuranceProviders = () => {
  return useQuery({
    queryKey: ["LIST_INSURANCE_PROVIDERS"],
    queryFn: async () => {
      return await mcsGqlClient.request(LIST_INSURANCE_PROVIDERS);
    },
    staleTime: 1000 * 60 * 60, // dont refetch for an hour (unless page refresh since we dont cache)
  });
};

type AllInsuranceProvidersQueryResult = ReturnType<typeof useAllInsuranceProviders>;

type InsuranceProviderChoicesRet = {
  isLoading: AllInsuranceProvidersQueryResult["isLoading"];
  isError: AllInsuranceProvidersQueryResult["isError"];
  choices: SelectInputChoice[];
};

export const useInsuranceProviderChoices = (): InsuranceProviderChoicesRet => {
  const { data, isLoading, isError } = useAllInsuranceProviders();

  return useMemo<InsuranceProviderChoicesRet>(() => {
    const choices: SelectInputChoice[] = [];

    data?.allInsuranceProviders?.edges.forEach((edge) => {
      if (!edge?.node) {
        return;
      }

      // MW-6088: Temporarily removing Molina of New Mexico option because we are not live yet
      if (edge.node.slug === "MOLINA_OF_NEW_MEXICO") {
        return;
      }

      choices.push({ label: edge.node.niceName, slug: edge.node.slug, value: edge.node.id });
    });

    return {
      choices,
      isLoading,
      isError,
    };
  }, [data, isLoading, isError]);
};
